/**
 * Mensagens pré-preenchidas do WhatsApp
 *
 * Regra de ouro: o texto é SEMPRE na voz do CLIENTE falando com a Emperc.
 * Emojis: ok — o link deve ser gerado via buildWhatsAppChatUrl (Web/API),
 * não via wa.me no app nativo do Windows (que costuma virar �).
 */

import { getProductFullUrl } from "../utils/productUrl";

function formatBRL(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function isAnonymousName(nome) {
  const n = String(nome || "")
    .trim()
    .toLowerCase();
  return !n || ["visitante", "visitor", "cliente", "customer", "guest"].includes(n);
}

/**
 * Saudação do cliente → Emperc
 */
export function customerGreeting(nomeCliente = "visitante", isPT = true) {
  if (isPT) {
    if (isAnonymousName(nomeCliente)) return "Olá, Emperc! 👋";
    return `Olá, Emperc! 👋\nSou *${nomeCliente.trim()}*.`;
  }
  if (isAnonymousName(nomeCliente)) return "Hi, Emperc! 👋";
  return `Hi, Emperc! 👋\nI'm *${nomeCliente.trim()}*.`;
}

/**
 * Saudação curta por período (voz do cliente — sem frases da loja)
 */
export const getTimeOfDay = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 18) return "afternoon";
  if (hour >= 18 && hour < 22) return "evening";
  return "night";
};

export const TIME_GREETINGS = {
  morning: {
    PT: (nome) => customerGreeting(nome, true),
    EN: (nome) => customerGreeting(nome, false),
  },
  afternoon: {
    PT: (nome) => customerGreeting(nome, true),
    EN: (nome) => customerGreeting(nome, false),
  },
  evening: {
    PT: (nome) => customerGreeting(nome, true),
    EN: (nome) => customerGreeting(nome, false),
  },
  night: {
    PT: (nome) => customerGreeting(nome, true),
    EN: (nome) => customerGreeting(nome, false),
  },
};

// ============================================
// TEMPLATES POR CONTEXTO (voz do cliente)
// ============================================

export const MESSAGES = {
  WELCOME: {
    PT: (nomeCliente) =>
      `${customerGreeting(nomeCliente, true)}\n\nGostaria de atendimento sobre peças e componentes da Emperc.\n\nPodem me ajudar?`,
    EN: (nomeCliente) =>
      `${customerGreeting(nomeCliente, false)}\n\nI'd like help with Emperc parts and components.\n\nCan you assist me?`,
  },

  CATALOG: {
    PT: (nomeCliente) =>
      `${customerGreeting(nomeCliente, true)}\n\nEstou no *catálogo* e preciso de ajuda para encontrar a peça certa.\n\nPodem me orientar sobre compatibilidade e disponibilidade?`,
    EN: (nomeCliente) =>
      `${customerGreeting(nomeCliente, false)}\n\nI'm browsing the *catalog* and need help finding the right part.\n\nCan you advise on compatibility and availability?`,
  },

  PRODUCT: {
    PT: (nomeCliente, productName) =>
      `${customerGreeting(nomeCliente, true)}\n\nTenho interesse nesta peça:\n\n📦 *${productName}*\n\nPodem confirmar estoque, compatibilidade e prazo?`,
    EN: (nomeCliente, productName) =>
      `${customerGreeting(nomeCliente, false)}\n\nI'm interested in this part:\n\n📦 *${productName}*\n\nCan you confirm stock, compatibility and lead time?`,
  },

  CART: {
    PT: (nomeCliente) =>
      `${customerGreeting(nomeCliente, true)}\n\nTenho dúvidas sobre o meu *carrinho*.\n\nPodem me ajudar com disponibilidade, pagamento ou frete?`,
    EN: (nomeCliente) =>
      `${customerGreeting(nomeCliente, false)}\n\nI have questions about my *cart*.\n\nCan you help with availability, payment or shipping?`,
  },

  CHECKOUT: {
    PT: (nomeCliente) =>
      `${customerGreeting(nomeCliente, true)}\n\nEstou no *checkout* e preciso de ajuda para finalizar.\n\nPodem me orientar?`,
    EN: (nomeCliente) =>
      `${customerGreeting(nomeCliente, false)}\n\nI'm at *checkout* and need help completing the order.\n\nCan you guide me?`,
  },

  EMPTY_CART: {
    PT: (nomeCliente) =>
      `${customerGreeting(nomeCliente, true)}\n\nEstou navegando no site e gostaria de ajuda para encontrar a peça certa para o meu modelo.`,
    EN: (nomeCliente) =>
      `${customerGreeting(nomeCliente, false)}\n\nI'm browsing the site and would like help finding the right part for my model.`,
  },
};

/** Mantido por compatibilidade — não deve ir no texto pré-preenchido do cliente */
export const BUSINESS_HOURS_MESSAGES = {
  ONLINE: {
    PT: `Horário: Seg–Sex 9h–18h · Sáb 9h–13h`,
    EN: `Hours: Mon–Fri 9am–6pm · Sat 9am–1pm`,
  },
  OFFLINE: {
    PT: `Fora do horário comercial — respondo assim que possível.`,
    EN: `Outside business hours — I'll reply as soon as possible.`,
  },
};

export const CALL_TO_ACTION = {
  PT: `Podem me ajudar?`,
  EN: `Can you help me?`,
};

export const CUSTOM_MESSAGES = {
  PROMO: {
    PT: `${customerGreeting("visitante", true)}\n\nVi condições especiais no site e gostaria de mais detalhes.`,
    EN: `${customerGreeting("visitante", false)}\n\nI saw special offers on the site and would like more details.`,
  },
  NEW_PRODUCT: {
    PT: `${customerGreeting("visitante", true)}\n\nVi uma peça nova no catálogo e gostaria de informações.`,
    EN: `${customerGreeting("visitante", false)}\n\nI saw a new part in the catalog and would like information.`,
  },
  THANK_YOU: {
    PT: `Obrigado pelo atendimento!`,
    EN: `Thank you for your help!`,
  },
  TECH_SUPPORT: {
    PT: `${customerGreeting("visitante", true)}\n\nPreciso de suporte técnico sobre compatibilidade / instalação de peça.`,
    EN: `${customerGreeting("visitante", false)}\n\nI need technical support about part compatibility / installation.`,
  },
};

// ============================================
// SAZONAL (opcional — só se o fluxo pedir)
// ============================================

const getLastFridayOfNovember = (year) => {
  const lastDay = new Date(year, 10, 30);
  const dayOfWeek = lastDay.getDay();
  const daysToSubtract = (dayOfWeek + 2) % 7;
  return 30 - daysToSubtract;
};

const getMothersDay = (year) => {
  const firstDay = new Date(year, 4, 1);
  const firstSunday = 8 - firstDay.getDay();
  return firstSunday + 7;
};

const getFathersDay = (year) => {
  const firstDay = new Date(year, 7, 1);
  const firstSunday = 8 - firstDay.getDay();
  return firstSunday + 7;
};

export const getSeasonalEvent = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const day = now.getDate();

  if (month === 11) {
    const lastFriday = getLastFridayOfNovember(now.getFullYear());
    if (Math.abs(day - lastFriday) <= 7) return "blackfriday";
  }
  if (month === 12) return "christmas";
  if (month === 12 && day >= 20) return "newyear";
  if (month === 1 && day <= 10) return "newyear";
  if (month === 5) {
    const mothersDay = getMothersDay(now.getFullYear());
    if (Math.abs(day - mothersDay) <= 7) return "mothersday";
  }
  if (month === 8) {
    const fathersDay = getFathersDay(now.getFullYear());
    if (Math.abs(day - fathersDay) <= 7) return "fathersday";
  }
  return null;
};

/** Mensagens sazonais na voz do cliente (não promoção da loja) */
export const SEASONAL_MESSAGES = {
  blackfriday: {
    PT: `Vi as condições de Black Friday e gostaria de mais informações.`,
    EN: `I saw the Black Friday deals and would like more information.`,
  },
  christmas: {
    PT: `Vi as ofertas de fim de ano e gostaria de mais informações.`,
    EN: `I saw the end-of-year offers and would like more information.`,
  },
  newyear: {
    PT: `Vi as condições de início de ano e gostaria de mais informações.`,
    EN: `I saw the new-year offers and would like more information.`,
  },
  mothersday: {
    PT: `Vi as condições especiais no site e gostaria de mais informações.`,
    EN: `I saw the special offers on the site and would like more information.`,
  },
  fathersday: {
    PT: `Vi as condições especiais no site e gostaria de mais informações.`,
    EN: `I saw the special offers on the site and would like more information.`,
  },
};

// ============================================
// PRODUTO (mensagem principal do PDP)
// ============================================

/**
 * Mensagem detalhada do produto — curta, clara, voz do cliente
 */
export const buildDetailedProductMessage = (
  product,
  nomeCliente = "visitante",
  isPT = true
) => {
  if (!product) {
    return isPT
      ? `${customerGreeting(nomeCliente, true)}\n\nGostaria de informações sobre uma peça.`
      : `${customerGreeting(nomeCliente, false)}\n\nI'd like information about a part.`;
  }

  const productUrl = getProductFullUrl(product);
  const lines = [customerGreeting(nomeCliente, isPT), ""];

  lines.push(
    isPT ? "Tenho interesse nesta peça:" : "I'm interested in this part:"
  );
  lines.push("");
  lines.push(`📦 *${product.name}*`);

  if (product.brand) {
    lines.push(
      isPT ? `🏷️ *Marca:* ${product.brand}` : `🏷️ *Brand:* ${product.brand}`
    );
  }

  if (Array.isArray(product.boardCodes) && product.boardCodes.length > 0) {
    const codes = product.boardCodes.slice(0, 4).join(" · ");
    lines.push(isPT ? `🔢 *Código:* ${codes}` : `🔢 *Code:* ${codes}`);
  }

  if (Array.isArray(product.tvModels) && product.tvModels.length > 0) {
    const models = product.tvModels.slice(0, 5).join(", ");
    const more =
      product.tvModels.length > 5
        ? ` (+${product.tvModels.length - 5})`
        : "";
    lines.push(
      isPT
        ? `📺 *Modelos:* ${models}${more}`
        : `📺 *Models:* ${models}${more}`
    );
  }

  if (product.category?.name) {
    lines.push(
      isPT
        ? `📂 *Categoria:* ${product.category.name}`
        : `📂 *Category:* ${product.category.name}`
    );
  }

  if (Number.isFinite(Number(product.price))) {
    lines.push(
      isPT
        ? `💰 *Preço:* ${formatBRL(product.price)}`
        : `💰 *Price:* ${formatBRL(product.price)}`
    );
  }

  if (product.stock !== undefined && product.stock !== null) {
    if (product.stock > 0) {
      lines.push(
        isPT
          ? `✅ *Estoque no site:* ${product.stock} un.`
          : `✅ *Stock on site:* ${product.stock} unit(s)`
      );
    } else {
      lines.push(
        isPT
          ? `⚠️ *Estoque no site:* indisponível`
          : `⚠️ *Stock on site:* unavailable`
      );
    }
  }

  if (productUrl) {
    lines.push("");
    lines.push(`🔗 ${productUrl}`);
  }

  lines.push("");
  if (isPT) {
    lines.push("Podem me confirmar, por favor:");
    lines.push("• Compatibilidade com o meu modelo");
    lines.push("• Disponibilidade e prazo de envio");
    lines.push("• Garantia");
    lines.push("");
    lines.push("Obrigado!");
  } else {
    lines.push("Could you please confirm:");
    lines.push("• Compatibility with my model");
    lines.push("• Availability and shipping time");
    lines.push("• Warranty");
    lines.push("");
    lines.push("Thank you!");
  }

  return lines.join("\n");
};

// ============================================
// FOLLOW-UP (ainda voz do cliente)
// ============================================

export const FOLLOW_UP_MESSAGES = {
  ADDED_TO_CART: {
    PT: (nomeCliente, productName) =>
      `${customerGreeting(nomeCliente, true)}\n\nAcabei de adicionar *${productName}* ao carrinho e gostaria de confirmar disponibilidade / compatibilidade antes de finalizar.`,
    EN: (nomeCliente, productName) =>
      `${customerGreeting(nomeCliente, false)}\n\nI just added *${productName}* to my cart and would like to confirm availability / compatibility before checkout.`,
  },

  PRODUCT_VIEWED: {
    PT: (nomeCliente, productName) =>
      `${customerGreeting(nomeCliente, true)}\n\nEstou vendo *${productName}* e tenho algumas dúvidas técnicas.\n\nPodem me ajudar?`,
    EN: (nomeCliente, productName) =>
      `${customerGreeting(nomeCliente, false)}\n\nI'm looking at *${productName}* and have a few technical questions.\n\nCan you help?`,
  },

  PROMO_IN_CART: {
    PT: (nomeCliente) =>
      `${customerGreeting(nomeCliente, true)}\n\nTenho peças no carrinho e gostaria de confirmar as condições e a disponibilidade antes de finalizar.`,
    EN: (nomeCliente) =>
      `${customerGreeting(nomeCliente, false)}\n\nI have parts in my cart and would like to confirm conditions and availability before checkout.`,
  },
};

export const isProductOnSale = (product) => {
  if (!product) return false;
  if (product.originalPrice && product.originalPrice > product.price) return true;
  if (product.onSale || product.promotion) return true;
  if (
    product.tags &&
    product.tags.some((tag) =>
      ["promoção", "promocao", "desconto", "sale", "off"].includes(
        String(tag).toLowerCase()
      )
    )
  ) {
    return true;
  }
  return false;
};

export const calculateDiscount = (product) => {
  if (product?.originalPrice && product.originalPrice > product.price) {
    return Math.round(
      ((product.originalPrice - product.price) / product.originalPrice) * 100
    );
  }
  return 0;
};

export const buildPromotionalMessage = (
  products = [],
  nomeCliente = "visitante",
  isPT = true
) => {
  const promotionalProducts = products.filter(isProductOnSale);
  if (promotionalProducts.length === 0) return null;

  let mensagem = `${customerGreeting(nomeCliente, isPT)}\n\n`;
  mensagem += isPT
    ? `Vi estas peças com condições especiais e gostaria de mais detalhes:\n\n`
    : `I saw these parts with special conditions and would like more details:\n\n`;

  promotionalProducts.slice(0, 3).forEach((product, index) => {
    mensagem += `${index + 1}. *${product.name}* — ${formatBRL(product.price)}\n`;
  });

  mensagem += isPT
    ? `\nPodem me confirmar disponibilidade?`
    : `\nCan you confirm availability?`;

  return mensagem;
};

export const MESSAGE_CONFIG = {
  USE_EMOJIS: true,
  INCLUDE_BUSINESS_HOURS: false,
  INCLUDE_CURRENT_URL: true,
  INCLUDE_CART_INFO: true,
  USE_TIME_GREETINGS: true,
  INCLUDE_SEASONAL_MESSAGES: false,
  INCLUDE_DETAILED_PRODUCT_INFO: true,
  ENABLE_FOLLOW_UP: true,
  DATE_FORMAT: "DD/MM/YYYY",
  TIME_FORMAT: "HH:mm",
};
